import React, { useEffect } from 'react';
import { Box, Typography, Button, CircularProgress } from '@mui/material';
import { vortxTypography } from '@vortx/shared-ui';
import { alpha } from '@mui/material/styles';
import { COLORS, fadeInUp } from '../results/ResultsDesignTokens';

const StatusCard = ({ title, children }) => (
    <Box sx={{
        p: 2.5,
        bgcolor: COLORS.bg.card,
        border: `1px solid ${alpha(COLORS.accent, 0.15)}`,
        borderRadius: 2,
        animation: `${fadeInUp} 0.4s ease-out`,
    }}>
        <Typography sx={{
            fontFamily: vortxTypography.mono,
            fontSize: '0.65rem',
            fontWeight: 600,
            textTransform: 'uppercase',
            letterSpacing: '0.15em',
            color: COLORS.text.muted,
            mb: 1.5,
        }}>
            {title}
        </Typography>
        {children}
    </Box>
);

const StatRow = ({ label, value, color }) => (
    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', py: 0.5 }}>
        <Typography sx={{
            fontFamily: vortxTypography.mono,
            fontSize: '0.75rem',
            color: COLORS.text.secondary,
        }}>
            {label}
        </Typography>
        <Typography sx={{
            fontFamily: vortxTypography.mono,
            fontSize: '0.85rem',
            fontWeight: 600,
            color: color || COLORS.text.primary,
        }}>
            {value}
        </Typography>
    </Box>
);

export default function DataPipelineTab({ service }) {
    const { comstockStats, samplingCoverage, downloadJobs, samplingJobs, loading } = service;

    useEffect(() => {
        service.refreshComstockStatus();
    }, []);

    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            {/* ComStock Cache Status */}
            <StatusCard title="ComStock Data Cache">
                {comstockStats ? (
                    <>
                        <StatRow
                            label="Total buildings"
                            value={comstockStats.total_buildings?.toLocaleString() || '0'}
                            color={COLORS.efficiency}
                        />
                        <StatRow
                            label="Building types"
                            value={comstockStats.buildings_by_type
                                ? Object.keys(comstockStats.buildings_by_type).length
                                : '0'
                            }
                        />
                        <StatRow
                            label="Quality OK"
                            value={comstockStats.quality_ok_pct
                                ? `${comstockStats.quality_ok_pct.toFixed(1)}%`
                                : 'N/A'
                            }
                        />
                        <StatRow
                            label="Cache size"
                            value={comstockStats.cache_size_mb
                                ? `${comstockStats.cache_size_mb.toFixed(0)} MB`
                                : 'N/A'
                            }
                        />
                    </>
                ) : (
                    <Typography sx={{
                        fontFamily: vortxTypography.mono,
                        fontSize: '0.75rem',
                        color: COLORS.text.muted,
                    }}>
                        No data cached yet
                    </Typography>
                )}

                <Box sx={{ mt: 2, display: 'flex', gap: 1 }}>
                    <Button
                        variant="outlined"
                        size="small"
                        onClick={() => service.startDownload('Warehouse')}
                        disabled={loading}
                        sx={{
                            fontFamily: vortxTypography.mono,
                            fontSize: '0.7rem',
                            color: COLORS.accent,
                            borderColor: alpha(COLORS.accent, 0.5),
                            '&:hover': {
                                borderColor: COLORS.accent,
                                bgcolor: alpha(COLORS.accent, 0.1),
                            },
                        }}
                    >
                        {loading ? <CircularProgress size={14} sx={{ mr: 1 }} /> : null}
                        Download Data
                    </Button>
                    <Button
                        variant="outlined"
                        size="small"
                        onClick={() => service.refreshComstockStatus()}
                        sx={{
                            fontFamily: vortxTypography.mono,
                            fontSize: '0.7rem',
                            color: COLORS.text.secondary,
                            borderColor: alpha(COLORS.text.secondary, 0.3),
                            '&:hover': {
                                borderColor: COLORS.text.secondary,
                                bgcolor: alpha(COLORS.text.secondary, 0.1),
                            },
                        }}
                    >
                        Refresh
                    </Button>
                </Box>
            </StatusCard>

            {/* Sampling Status */}
            <StatusCard title="Stratified Sample">
                {samplingCoverage?.sample_stats ? (
                    <>
                        <StatRow
                            label="Total cells"
                            value={samplingCoverage.sample_stats.total_cells}
                        />
                        <StatRow
                            label="Cells at target"
                            value={samplingCoverage.sample_stats.cells_with_target}
                        />
                        <StatRow
                            label="Fill rate"
                            value={`${(samplingCoverage.sample_stats.fill_rate * 100).toFixed(0)}%`}
                            color={samplingCoverage.sample_stats.fill_rate > 0.8
                                ? COLORS.efficiency
                                : COLORS.solar
                            }
                        />
                    </>
                ) : (
                    <Typography sx={{
                        fontFamily: vortxTypography.mono,
                        fontSize: '0.75rem',
                        color: COLORS.text.muted,
                    }}>
                        No sample built yet
                    </Typography>
                )}

                <Box sx={{ mt: 2 }}>
                    <Button
                        variant="outlined"
                        size="small"
                        onClick={() => service.startSampling({ buildings_per_cell: 5 })}
                        disabled={loading}
                        sx={{
                            fontFamily: vortxTypography.mono,
                            fontSize: '0.7rem',
                            color: COLORS.accent,
                            borderColor: alpha(COLORS.accent, 0.5),
                            '&:hover': {
                                borderColor: COLORS.accent,
                                bgcolor: alpha(COLORS.accent, 0.1),
                            },
                        }}
                    >
                        {loading ? <CircularProgress size={14} sx={{ mr: 1 }} /> : null}
                        Build Sample
                    </Button>
                </Box>
            </StatusCard>

            {/* Coverage Matrix (if available) */}
            {samplingCoverage?.coverage_matrix && (
                <StatusCard title="Coverage Matrix">
                    <Box sx={{ overflowX: 'auto' }}>
                        <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                            {samplingCoverage.coverage_matrix.building_types?.map(btype => (
                                <Box key={btype} sx={{
                                    px: 1,
                                    py: 0.5,
                                    bgcolor: alpha(COLORS.accent, 0.1),
                                    borderRadius: 1,
                                    border: `1px solid ${alpha(COLORS.accent, 0.2)}`,
                                }}>
                                    <Typography sx={{
                                        fontFamily: vortxTypography.mono,
                                        fontSize: '0.65rem',
                                        color: COLORS.text.secondary,
                                    }}>
                                        {btype}
                                    </Typography>
                                </Box>
                            ))}
                        </Box>
                    </Box>
                </StatusCard>
            )}
        </Box>
    );
}
